import mixpanel, {Dict} from 'mixpanel-browser'
import {User} from './CurrentUserContext'

// Session ID for tracking user sessions
let sessionId: string | null = null

const generateSessionId = () => {
  return `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
}

class Analytics {
  private initialized = false
  private currentUser: User | null = null

  /**
   * Initialize Mixpanel with project token
   * Call this once when the app loads
   */
  init(token: string) {
    if (this.initialized) {
      return
    }

    try {
      mixpanel.init(token, {
        debug: process.env.NODE_ENV === 'development',
        track_pageview: false, // We'll track page views manually
        persistence: 'localStorage',
        ignore_dnt: false,
        // Session Replay configuration
        record_sessions_percent: 100, // Record 100% of sessions
        record_idle_timeout_ms: 1800000, // End replay after 30 mins of inactivity
        record_max_ms: 86400000, // Maximum replay length of 24 hours
        record_block_selector: '', // Don't block any elements by default
        record_mask_text_selector: '*', // Mask all text by default for privacy
      })

      sessionId = generateSessionId()
      this.initialized = true

      if (process.env.NODE_ENV === 'development') {
        console.log('[Analytics] Mixpanel initialized with Session Replay')
      }
    } catch (error) {
      console.error('[Analytics] Failed to initialize Mixpanel:', error)
    }
  }

  /**
   * Identify user and set user properties
   * Call this after successful authentication
   */
  identify(user: User, auth0UserId?: string) {
    if (!this.initialized) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[Analytics] Mixpanel not initialized')
      }
      return
    }

    try {
      this.currentUser = user

      // Identify user with their Contimo user ID
      mixpanel.identify(user._id)

      // Set user properties
      mixpanel.people.set({
        $name: user.fullname,
        $email: user.email,
        userId: user._id,
        companyId: user.companyId,
        companyName: user.company.name,
        companyType: user.company.type,
        userRole: user.role,
        isAdmin: user.role === 'AccountAdmin',
        allowedMarketplaces: user.company.allowedMarketplaces,
        allowFillerCampaigns: user.company.allowFillerCampaigns,
        allowInHouseCampaigns: user.company.allowInHouseCampaigns,
        allowMarketplaceNativeCampaigns: user.company.allowMarketplaceNativeCampaigns,
        allowInHouseNativeCampaigns: user.company.allowInHouseNativeCampaigns,
        allowVevIntegration: user.company.allowVevIntegration,
        auth0UserId: auth0UserId || null,
      })

      if (process.env.NODE_ENV === 'development') {
        console.log('[Analytics] User identified:', user._id)
      }
    } catch (error) {
      console.error('[Analytics] Failed to identify user:', error)
    }
  }

  /**
   * Track an event with optional properties
   */
  track(eventName: string, properties?: Dict) {
    if (!this.initialized) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[Analytics] Mixpanel not initialized')
      }
      return
    }

    try {
      const enrichedProperties = {
        ...properties,
        // Add common context properties
        timestamp: new Date().toISOString(),
        page: window.location.pathname,
        sessionId: sessionId,
        // Add user context if available
        ...(this.currentUser
          ? {
              companyType: this.currentUser.company.type,
              userRole: this.currentUser.role,
              companyId: this.currentUser.companyId,
            }
          : {}),
      }

      mixpanel.track(eventName, enrichedProperties)

      if (process.env.NODE_ENV === 'development') {
        console.log('[Analytics] Event tracked:', eventName, enrichedProperties)
      }
    } catch (error) {
      console.error('[Analytics] Failed to track event:', error)
    }
  }

  /**
   * Track page view
   */
  trackPageView(pageName?: string) {
    const page = pageName || window.location.pathname
    this.track('page_viewed', {
      pageName: page,
      url: window.location.href,
      referrer: document.referrer,
    })
  }

  /**
   * Reset user session (call on logout)
   */
  reset() {
    if (!this.initialized) {
      return
    }

    try {
      mixpanel.reset()
      this.currentUser = null
      sessionId = generateSessionId()

      if (process.env.NODE_ENV === 'development') {
        console.log('[Analytics] Session reset')
      }
    } catch (error) {
      console.error('[Analytics] Failed to reset session:', error)
    }
  }

  /**
   * Set super properties (properties sent with every event)
   */
  setSuperProperties(properties: Dict) {
    if (!this.initialized) {
      return
    }

    try {
      mixpanel.register(properties)
    } catch (error) {
      console.error('[Analytics] Failed to set super properties:', error)
    }
  }

  /**
   * Time an event (start)
   */
  timeEvent(eventName: string) {
    if (!this.initialized) {
      return
    }

    try {
      mixpanel.time_event(eventName)
    } catch (error) {
      console.error('[Analytics] Failed to time event:', error)
    }
  }

  /**
   * Increment a user property
   */
  incrementUserProperty(property: string, value: number = 1) {
    if (!this.initialized) {
      return
    }

    try {
      mixpanel.people.increment(property, value)
    } catch (error) {
      console.error('[Analytics] Failed to increment user property:', error)
    }
  }

  /**
   * Check if Mixpanel is initialized
   */
  isInitialized() {
    return this.initialized
  }
}

// Export singleton instance
export const analytics = new Analytics()

// Export convenience functions
export const trackEvent = (eventName: string, properties?: Dict) =>
  analytics.track(eventName, properties)

export const trackPageView = (pageName?: string) =>
  analytics.trackPageView(pageName)

export const identifyUser = (user: User, auth0UserId?: string) =>
  analytics.identify(user, auth0UserId)

export const resetAnalytics = () => analytics.reset()

export const timeEvent = (eventName: string) => analytics.timeEvent(eventName)

export const incrementUserProperty = (property: string, value?: number) =>
  analytics.incrementUserProperty(property, value)
